import type { Language } from '../contexts/LanguageContext';
import type { GenerationFormat } from './cupScoresData';

export interface CupScoresStrings {
  documentTitle: string;
  brandYear: string;
  pageTitle: string;
  pageSubtitle: string;
  genTab: string;
  navJumpLabel: string;
  navSchedule: string;
  navTables: string;
  navGroupA: string;
  navGroupB: string;
  navGroupC: string;
  navThirds: string;
  navBracket: string;
  scheduleTitle: string;
  scheduleTitleOnly: string;
  scheduleSub: string;
  scheduleSubAdmin: string;
  scheduleLoading: string;
  scheduleTeamFilterLabel: string;
  scheduleTeamFilterPlaceholder: string;
  scheduleTeamFilterClear: string;
  scheduleTeamFilterActive: (team: string) => string;
  scheduleTeamFilterEmpty: string;
  pitch: string;
  groupPrefix: string;
  standingsTitle: string;
  standingsSub: string;
  standingsSubLeagueTable: string;
  standingsSubLeaguePlayoffs: string;
  standingsSubAdmin: string;
  navPlacements: string;
  teamNamePlaceholder: string;
  teamSlotHint: string;
  sfVsWinnerF: string;
  sfVsWinnerE: string;
  thirdsTitle: string;
  thirdsSubQuarterFinals: string;
  thirdsSubQuarterFinals2017: string;
  thirdsSubSemiDirect: string;
  thirdQualified: string;
  thirdNotQualified: string;
  thirdPlayoff: string;
  bracketTitle: string;
  bracketQfTitle: string;
  bracketPlayoffTitle: string;
  bracketSemi1Pair: string;
  bracketSemi2Pair: string;
  bracketSemiDirectSf1: string;
  bracketSemiDirectSf2: string;
  bracketLeagueTableSub: string;
  bracketLeaguePlayoffsSub: string;
  placementChampion: string;
  placementRunnerUp: string;
  placementThird: string;
  thirdsSubWithinGroup: string;
  thTeam: string;
  thP: string;
  thW: string;
  thD: string;
  thL: string;
  thGF: string;
  thGA: string;
  thGD: string;
  thPts: string;
  thRank: string;
  thGroup: string;
  thStatus: string;
  scoreHint: string;
  themeUseLight: string;
  themeUseDark: string;
  themeToggleGroup: string;
  koLabels: Record<
    'po1' | 'po2' | 'qf1' | 'qf2' | 'qf3' | 'qf4' | 'sf1' | 'sf2' | 'final' | 'third',
    string
  >;
  koQfPlaceholders: Record<'qf1' | 'qf2' | 'qf3' | 'qf4', string>;
  koPlayoffPlaceholders: Record<'po1' | 'po2', string>;
  documentTitleAdmin: string;
  documentTitleAdminGate: string;
  adminGateTitle: string;
  adminGateLead: string;
  adminPasswordLabel: string;
  adminSubmit: string;
  adminError: string;
  adminLogout: string;
  exportPdf: string;
  exportPdfAria: string;
  pdfTitle: string;
  pdfSubtitle: string;
  pdfColTime: string;
  pdfColPitch: string;
  pdfColMatch: string;
  pdfColPhase: string;
  pdfPhaseMiniLeague: string;
  pdfGenLabel: string;
  pdfPitchesLabel: string;
  pdfFooter: string;
  medalCeremonyTitle: string;
  medalCeremonyLead: string;
  medalCeremonyNote2014: string;
  koManualSectionTitle: string;
  koManualSectionLead: string;
  koManualTeamHint: string;
  koManualFillFromAuto: string;
  koManualClear: string;
  koManualHome: string;
  koManualAway: string;
  koManualIncompleteHint: string;
  koManualPlayoffPair: (letter: string) => string;
  koManualSfPair: (n: 1 | 2) => string;
  koPenaltiesLabel: string;
  koPenaltiesHint: string;
}

const EN: CupScoresStrings = {
  documentTitle: 'Junior Cup 2026 — Results',
  brandYear: 'Junior Cup 2026',
  pageTitle: 'Junior Cup 2026',
  pageSubtitle: 'Pick your age group, then schedule, tables, and knock-out.',
  genTab: 'Born',
  navJumpLabel: 'Jump to',
  navSchedule: 'Schedule',
  navTables: 'Tables',
  navGroupA: 'Group A',
  navGroupB: 'Group B',
  navGroupC: 'Group C',
  navThirds: '3rd places',
  navBracket: 'Knock-out pairs',
  scheduleTitle: 'Schedule & knock-out',
  scheduleTitleOnly: 'Schedule',
  scheduleSub: 'Play 09:00–17:00 · 20 min per match. Kick-off times and pitches — enter goals to update tables.',
  scheduleSubAdmin:
    'Team names and goals: type, then Enter or click outside the field to save. Tables and knock-out update after save.',
  scheduleLoading: 'Loading schedule…',
  scheduleTeamFilterLabel: 'Team schedule',
  scheduleTeamFilterPlaceholder: 'Search team name (e.g. Junior NS)…',
  scheduleTeamFilterClear: 'Show all matches',
  scheduleTeamFilterActive: (team) => `Showing timetable for ${team}`,
  scheduleTeamFilterEmpty: 'No matches found for this team in the selected age group.',
  pitch: 'Pitch',
  groupPrefix: 'Group',
  standingsTitle: 'Tables',
  standingsSub: 'Tables follow the scores you enter in the schedule.',
  standingsSubLeagueTable:
    'Mini league: 1st place = winners, 3rd place = bronze — decided by the table after all matches.',
  standingsSubLeaguePlayoffs:
    'Mini league: everyone plays everyone. 1st vs 2nd play the final for the title; 3rd vs 4th play for bronze.',
  standingsSubAdmin:
    'Type a team name, then press Enter or click outside the field to save. Leave blank to keep the slot code (A1, B2, …).',
  navPlacements: 'Final standings',
  teamNamePlaceholder: 'Team name',
  teamSlotHint: 'slot',
  sfVsWinnerF: 'winner of F',
  sfVsWinnerE: 'winner of E',
  thirdsTitle: 'Third-placed teams',
  thirdsSubQuarterFinals:
    'Live table across groups — ranked by points, then goal difference, then goals scored. Top two advance to the quarter-finals.',
  thirdsSubQuarterFinals2017:
    'Live table — Group A 3rd place is ranked without matches vs the team that finishes 5th in Group A. Top two advance to the quarter-finals.',
  thirdsSubSemiDirect:
    'Cross-group play-offs only (2nd vs 3rd from the other group); group winners go straight to the semi-finals.',
  thirdsSubWithinGroup:
    'Within each group: 2nd vs 3rd play-off for a semi-final place; group winners go straight to the semi-finals.',
  bracketLeagueTableSub: 'No extra matches — final order from the mini-league table.',
  bracketLeaguePlayoffsSub:
    'After the mini league, the top four play two matches on the schedule: a final (1st vs 2nd) and a match for 3rd place (3rd vs 4th).',
  placementChampion: '1st place (winners)',
  placementRunnerUp: '2nd place',
  placementThird: '3rd place',
  thirdQualified: 'Quarter-finals',
  thirdNotQualified: 'Out',
  thirdPlayoff: 'Play-off for semi-finals',
  bracketTitle: 'Knock-out bracket',
  bracketQfTitle: 'Quarter-finals',
  bracketPlayoffTitle: 'Play-offs (semi-final places)',
  bracketSemi1Pair: 'Semi-final 1 · winners of E and F',
  bracketSemi2Pair: 'Semi-final 2 · winners of G and H',
  bracketSemiDirectSf1: 'Semi-final 1 · A1 vs winner of F (B2–A3)',
  bracketSemiDirectSf2: 'Semi-final 2 · B1 vs winner of E (A2–B3)',
  thTeam: 'Team',
  thP: 'P',
  thW: 'W',
  thD: 'D',
  thL: 'L',
  thGF: 'GF',
  thGA: 'GA',
  thGD: 'GD',
  thPts: 'Pts',
  thRank: '#',
  thGroup: 'Grp',
  thStatus: 'Status',
  scoreHint: 'Goals',
  themeUseLight: 'Switch to light theme',
  themeUseDark: 'Switch to dark theme',
  themeToggleGroup: 'Appearance',
  koLabels: {
    po1: 'Play-off A',
    po2: 'Play-off B',
    qf1: 'Quarter-final 1',
    qf2: 'Quarter-final 2',
    qf3: 'Quarter-final 3',
    qf4: 'Quarter-final 4',
    sf1: 'Semi-final 1',
    sf2: 'Semi-final 2',
    final: 'Final',
    third: '3rd place',
  },
  koQfPlaceholders: {
    qf1: 'A1 vs III1',
    qf2: 'B2 vs C2',
    qf3: 'III2 vs B1',
    qf4: 'C1 vs A2',
  },
  koPlayoffPlaceholders: {
    po1: 'A2 vs B3',
    po2: 'B2 vs A3',
  },
  documentTitleAdmin: 'Junior Cup 2026 — admin',
  documentTitleAdminGate: 'Junior Cup 2026 — admin login',
  adminGateTitle: 'Admin · cup scores',
  adminGateLead: 'Enter the club password to add or edit match results.',
  adminPasswordLabel: 'Password',
  adminSubmit: 'Sign in',
  adminError: 'Incorrect password.',
  adminLogout: 'Sign out',
  exportPdf: 'PDF schedule',
  exportPdfAria: 'Download PDF schedule for the selected age group',
  pdfTitle: 'Junior Cup 2026 — Schedule',
  pdfSubtitle: 'Play 09:00–17:00 · 20 min per match · grouped by day and age group',
  pdfColTime: 'Time',
  pdfColPitch: 'Pitch',
  pdfColMatch: 'Match',
  pdfColPhase: 'Round',
  pdfPhaseMiniLeague: 'Mini league',
  pdfGenLabel: 'Born',
  pdfPitchesLabel: 'Pitches',
  pdfFooter: 'Junior Cup 2026',
  medalCeremonyTitle: 'Medals & trophies',
  medalCeremonyLead: 'Starts 5 minutes after the final match of this age group ends.',
  medalCeremonyNote2014:
    'For 2014 (mini league), about 20 minutes is reserved for the ceremony — the end time shown is approximate.',
  koManualSectionTitle: 'Manual knock-out pairs (admin)',
  koManualSectionLead:
    'If automatic pairing is wrong, enter team slot codes (A1, B2, …) or the same names as in the table. All fields in a block must be filled before the manual bracket applies.',
  koManualTeamHint: 'Slot or team id',
  koManualFillFromAuto: 'Fill from automatic bracket',
  koManualClear: 'Clear manual pairs (use automatic)',
  koManualHome: 'Home',
  koManualAway: 'Away',
  koManualIncompleteHint: 'Complete every home/away field in this block to activate manual pairs.',
  koManualPlayoffPair: (letter) => `Play-off ${letter}`,
  koManualSfPair: (n) => `Semi-final ${n}`,
  koPenaltiesLabel: 'Penalties',
  koPenaltiesHint: 'penalty shoot-out score',
};

const SR: CupScoresStrings = {
  documentTitle: 'Junior Cup 2026 — Rezultati',
  brandYear: 'Junior Cup 2026',
  pageTitle: 'Junior Cup 2026',
  pageSubtitle: 'Izaberi godište — raspored, tabele i nokaut.',
  genTab: 'Godište',
  navJumpLabel: 'Skoči na',
  navSchedule: 'Raspored',
  navTables: 'Tabele',
  navGroupA: 'Grupa A',
  navGroupB: 'Grupa B',
  navGroupC: 'Grupa C',
  navThirds: 'Trećeplasirane',
  navBracket: 'Nokaut parovi',
  scheduleTitle: 'Raspored i nokaut',
  scheduleTitleOnly: 'Raspored',
  scheduleSub: 'Igra 09:00–17:00 · utakmice po 20 min. Počeci i tereni — unesi golove da se ažuriraju tabele.',
  scheduleSubAdmin:
    'Imena i golovi: unesi, pa Enter ili klik van polja da sačuvaš. Tabele i nokaut se ažuriraju posle čuvanja.',
  scheduleLoading: 'Učitavanje rasporeda…',
  scheduleTeamFilterLabel: 'Satnica ekipe',
  scheduleTeamFilterPlaceholder: 'Pretraži ime ekipe (npr. Junior NS)…',
  scheduleTeamFilterClear: 'Svi mečevi',
  scheduleTeamFilterActive: (team) => `Satnica za: ${team}`,
  scheduleTeamFilterEmpty: 'Nema utakmica za ovu ekipu u izabranom godištu.',
  pitch: 'Teren',
  groupPrefix: 'Grupa',
  standingsTitle: 'Tabele',
  standingsSub: 'Tabele prate rezultate iz rasporeda.',
  standingsSubLeagueTable:
    'Mini liga: 1. mesto = pobednik, 3. mesto = bronza — određuje tabela posle svih utakmica.',
  standingsSubLeaguePlayoffs:
    'Mini liga: svako sa svakim. Finale (1. protiv 2.) donosi titulu; utakmica za 3. mesto (3. protiv 4.) donosi bronzu.',
  standingsSubAdmin:
    'Upiši ime ekipe, pa Enter ili klik van polja da sačuvaš. Prazno polje = ostaje oznaka (A1, B2, …).',
  navPlacements: 'Konačan plasman',
  teamNamePlaceholder: 'Ime ekipe',
  teamSlotHint: 'oznaka',
  sfVsWinnerF: 'pobednik F',
  sfVsWinnerE: 'pobednik E',
  thirdsTitle: 'Trećeplasirane ekipe',
  thirdsSubQuarterFinals:
    'Živa tabela — rang: bodovi, pa gol-razlika, pa više postignutih golova. Najbolje dve prolaze u četvrtfinale.',
  thirdsSubQuarterFinals2017:
    'Živa tabela — za 3. mesto u grupi A ne računaju se utakmice protiv ekipe koja završi na 5. mestu u grupi A. Najbolje dve prolaze u četvrtfinale.',
  thirdsSubSemiDirect:
    'Baraž samo između grupa (2. iz jedne protiv 3. iz druge); prvoplasirani idu direktno u polufinale.',
  thirdsSubWithinGroup:
    'U svakoj grupi: 2. protiv 3. baraž za polufinale; prvoplasirani direktno u polufinale.',
  bracketLeagueTableSub: 'Bez dodatnih utakmica — konačan redosled prema tabeli mini lige.',
  bracketLeaguePlayoffsSub:
    'Posle mini lige, prve četiri ekipe igraju dve utakmice po rasporedu: finale (1. protiv 2.) i meč za 3. mesto (3. protiv 4.).',
  placementChampion: '1. mesto (pobednik)',
  placementRunnerUp: '2. mesto',
  placementThird: '3. mesto',
  thirdQualified: 'Četvrtfinale',
  thirdNotQualified: 'Van nokauta',
  thirdPlayoff: 'Baraž za polufinale',
  bracketTitle: 'Nokaut šema',
  bracketQfTitle: 'Četvrtfinala',
  bracketPlayoffTitle: 'Baraž (mesta u polufinalu)',
  bracketSemi1Pair: 'Polufinale 1 · pobednici E i F',
  bracketSemi2Pair: 'Polufinale 2 · pobednici G i H',
  bracketSemiDirectSf1: 'Polufinale 1 · A1 protiv pobednika F (B2–A3)',
  bracketSemiDirectSf2: 'Polufinale 2 · B1 protiv pobednika E (A2–B3)',
  thTeam: 'Ekipa',
  thP: 'U',
  thW: 'P',
  thD: 'N',
  thL: 'I',
  thGF: 'DG',
  thGA: 'PG',
  thGD: 'RG',
  thPts: 'Bod',
  thRank: '#',
  thGroup: 'Gr',
  thStatus: 'Prolaz',
  scoreHint: 'Golovi',
  themeUseLight: 'Svetla tema',
  themeUseDark: 'Tamna tema',
  themeToggleGroup: 'Izgled',
  koLabels: {
    po1: 'Baraž A',
    po2: 'Baraž B',
    qf1: 'Četvrtfinale 1',
    qf2: 'Četvrtfinale 2',
    qf3: 'Četvrtfinale 3',
    qf4: 'Četvrtfinale 4',
    sf1: 'Polufinale 1',
    sf2: 'Polufinale 2',
    final: 'Finale',
    third: 'Za 3. mesto',
  },
  koQfPlaceholders: {
    qf1: 'A1 protiv III1',
    qf2: 'B2 protiv C2',
    qf3: 'III2 protiv B1',
    qf4: 'C1 protiv A2',
  },
  koPlayoffPlaceholders: {
    po1: 'A2 protiv B3',
    po2: 'B2 protiv A3',
  },
  documentTitleAdmin: 'Junior kup 2026 — admin',
  documentTitleAdminGate: 'Junior kup 2026 — prijava admina',
  adminGateTitle: 'Admin · unos rezultata',
  adminGateLead: 'Unesi lozinku kluba da dodaješ ili menjaš golove.',
  adminPasswordLabel: 'Lozinka',
  adminSubmit: 'Prijava',
  adminError: 'Pogrešna lozinka.',
  adminLogout: 'Odjava',
  exportPdf: 'PDF raspored',
  exportPdfAria: 'Preuzmi PDF raspored za izabrano godište',
  pdfTitle: 'Junior kup 2026 — Raspored i satnica',
  pdfSubtitle: 'Igra 09:00–17:00 · utakmice po 20 min · po danima i godištima',
  pdfColTime: 'Vreme',
  pdfColPitch: 'Teren',
  pdfColMatch: 'Utakmica',
  pdfColPhase: 'Faza',
  pdfPhaseMiniLeague: 'Mini liga',
  pdfGenLabel: 'Godište',
  pdfPitchesLabel: 'Tereni',
  pdfFooter: 'Junior kup 2026',
  medalCeremonyTitle: 'Dodela medalja i trofeja',
  medalCeremonyLead: 'Počinje 5 minuta posle završetka poslednje utakmice ovog godišta.',
  medalCeremonyNote2014:
    'Za godište 2014 (mini liga) rezervisano je oko 20 minuta za dodelu; prikazani kraj je procenjen.',
  koManualSectionTitle: 'Ručni nokaut parovi (admin)',
  koManualSectionLead:
    'Ako automatski parovi nisu tačni, unesi oznake mesta (A1, B2, …) ili ista imena kao u tabeli. Sva polja u bloku moraju biti popunjena da bi ručna šema važila.',
  koManualTeamHint: 'Oznaka ili id ekipe',
  koManualFillFromAuto: 'Preuzmi iz automatskog proračuna',
  koManualClear: 'Obriši ručno (vrati automatski)',
  koManualHome: 'Domaćin',
  koManualAway: 'Gost',
  koManualIncompleteHint: 'Popuni sva polja u ovom bloku da bi ručni parovi bili na snazi.',
  koManualPlayoffPair: (letter) => `Baraž ${letter}`,
  koManualSfPair: (n) => `Polufinale ${n}`,
  koPenaltiesLabel: 'Penali',
  koPenaltiesHint: 'rezultat penala',
};

export function getCupScoresLocale(lang: Language): CupScoresStrings {
  if (lang === 'sr') return SR;
  return EN;
}

export function thirdsSubtitle(
  L: CupScoresStrings,
  format: GenerationFormat,
  generation?: import('./cupScoresData').GenerationYear
): string {
  if (format.knockout === 'quarter_finals') {
    if (generation === 2017) return L.thirdsSubQuarterFinals2017;
    return L.thirdsSubQuarterFinals;
  }
  if (format.semiDirectPlayoffs === 'within_group') return L.thirdsSubWithinGroup;
  return L.thirdsSubSemiDirect;
}

export function standingsSubtitle(L: CupScoresStrings, format: GenerationFormat): string {
  if (format.knockout === 'league_table') return L.standingsSubLeagueTable;
  if (format.knockout === 'league_playoffs') return L.standingsSubLeaguePlayoffs;
  return L.standingsSub;
}

export function scheduleSectionTitle(L: CupScoresStrings, format: GenerationFormat): string {
  if (format.knockout === 'league_table') return L.scheduleTitleOnly;
  return L.scheduleTitle;
}

/** Generic knock-out slot labels until the participant is known from results. */
export interface CupScoresRoleLabels {
  roleWinner: (group: string) => string;
  roleRunnerUp: (group: string) => string;
  roleThirdInGroup: (group: string) => string;
  roleThirdRank: (rank: number) => string;
  rolePlayoffWinner: (letter: string) => string;
}

const ROLE_EN: CupScoresRoleLabels = {
  roleWinner: (g) => `Winner · Group ${g}`,
  roleRunnerUp: (g) => `Runner-up · Group ${g}`,
  roleThirdInGroup: (g) => `3rd place · Group ${g}`,
  roleThirdRank: (n) => `Best 3rd place (${n})`,
  rolePlayoffWinner: (letter) => `Winner of ${letter}`,
};

const ROLE_SR: CupScoresRoleLabels = {
  roleWinner: (g) => `Prvoplasirani · grupa ${g}`,
  roleRunnerUp: (g) => `Drugoplasirani · grupa ${g}`,
  roleThirdInGroup: (g) => `Trećeplasirani · grupa ${g}`,
  roleThirdRank: (n) => `Najbolja trećaplasirana (${n})`,
  rolePlayoffWinner: (letter) => `Pobednik ${letter}`,
};

export function getCupScoresRoleLabels(lang: Language): CupScoresRoleLabels {
  return lang === 'sr' ? ROLE_SR : ROLE_EN;
}
